import { useState } from 'react';

import { keepPreviousData } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import { EditIcon, EyeIcon, FilterIcon, PlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import Error from '../../../../components/feedback/error.jsx';
import Loading from '../../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../../components/table/data-table-column-header.jsx';
import { DataTableFooter } from '../../../../components/table/data-table-footer.jsx';
import {
  DataTableFacetedFilter,
  DataTableToolbar,
} from '../../../../components/table/data-table-toolbar.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import { Badge } from '../../../../components/ui/badge.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import { DebouncedInput } from '../../../../components/ui/input-debounced.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { useTable } from '../../../../hooks/use-table.jsx';
import {
  CATALYST_USER_ROLES,
  TABLE_FILTER_LIKE,
} from '../../../../utils/constants.js';

const TABLE_COLUMNS = [
  {
    title: 'Id',
    accessorKey: 'id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Id" />
    ),
    cell: ({ row }) => (
      <Link to={row.getValue('id')}>
        <div className="underline">{row.getValue('id')}</div>
      </Link>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Nome',
    accessorKey: 'fullName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nome" />
    ),
    cell: ({ row }) => <div>{row.getValue('fullName')}</div>,
    enableSorting: false,
    enableHiding: false,
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    title: 'Email',
    accessorKey: 'email',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    cell: ({ row }) => {
      return <div className="flex space-x-2">{row.getValue('email')}</div>;
    },
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    title: 'Função',
    accessorKey: 'role',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Função" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex space-x-2">
          {Object.values(CATALYST_USER_ROLES).find(
            (role) => role === row.getValue('role'),
          )}
        </div>
      );
    },
    filterFn: (row, _, value) => {
      return value.includes(
        Object.values(CATALYST_USER_ROLES).find(
          (role) => role === row.getValue('role'),
        ).id,
      );
    },
  },
  {
    title: 'Estado',
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      return row.getValue('status') ? (
        <Badge variant="success">Ativo</Badge>
      ) : (
        <Badge variant="destructive">Inativo</Badge>
      );
    },
    filterFn: (row, _, value) => {
      return value.includes(row.getValue('status'));
    },
  },
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue('createdTime');

      if (!dateString) {
        return <div>---</div>;
      }

      try {
        const inputFormat = 'yyyy-MM-dd HH:mm:ss:SSS';
        const dateObject = parse(dateString, inputFormat, new Date());

        return <div>{format(dateObject, 'dd-MM-yyyy HH:mm:ss')}</div>;
      } catch (error) {
        console.error('Failed to parse date:', dateString, error);

        return <div>---</div>;
      }
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex flex-row space-x-2">
        <Link to={row.getValue('id')}>
          <Button
            variant="outline"
            className="h-auto p-2"
            aria-label="Ver utilizador"
          >
            <EyeIcon />
          </Button>
        </Link>
        <Link to={`${row.getValue('id')}/edit`}>
          <Button
            variant="outline"
            className="h-auto p-2"
            aria-label="Editar utilizador"
          >
            <EditIcon />
          </Button>
        </Link>
      </div>
    ),
  },
];

export function StaffList() {
  const [selectColumnFilter, setSelectColumnFilter] = useState('fullName');

  const {
    table,
    query: queryUsers,
    filters,
  } = useTable(
    TABLE_COLUMNS,
    {
      queryKey: ['users'],
      queryFn: async () => {
        const { data: staffUsers } = await msCatalystApi.get(
          `staff/paginate?${filters}`,
        );

        return staffUsers.data;
      },
      placeholderData: keepPreviousData,
      select: (data) => {
        return {
          ...data,
          rows: data.rows.map((item) => ({
            ...item,
            id: item.ROWID,
            createdTime: item.CREATEDTIME,
          })),
        };
      },
    },
    {
      sorting: [
        {
          id: 'createdTime',
          desc: true,
        },
      ],
    },
  );

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <Heading>Utilizadores</Heading>

        <div className="flex flex-row flex-wrap justify-end gap-4">
          <Button asChild>
            <Link to="create">
              <PlusIcon />
              Criar
            </Link>
          </Button>
        </div>
      </div>

      {queryUsers.isLoading || queryUsers.isPending ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <Loading />
        </div>
      ) : queryUsers.isError ? (
        <Error />
      ) : (
        <div className="space-y-4">
          <DataTableToolbar table={table}>
            <Select
              value={selectColumnFilter}
              onValueChange={(e) => {
                setSelectColumnFilter(e);
              }}
            >
              <SelectTrigger className="w-auto">
                <FilterIcon className="mr-2 size-4" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent>
                {[
                  { label: 'Nome', value: 'fullName' },
                  { label: 'Email', value: 'email' },
                ].map((item, i) => (
                  <SelectItem key={i} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DebouncedInput
              placeholder={`Pesquisar...`}
              value={
                table.getColumn(selectColumnFilter)?.getFilterValue() ?? ''
              }
              onChange={(val) =>
                table.getColumn(selectColumnFilter)?.setFilterValue(val)
              }
              className="h-10 md:w-[255px]"
            />

            {table.getColumn('role') && (
              <DataTableFacetedFilter
                column={table.getColumn('role')}
                title="Função"
                options={[
                  {
                    label: CATALYST_USER_ROLES.admin,
                    value: CATALYST_USER_ROLES.admin,
                  },
                  {
                    label: CATALYST_USER_ROLES.user,
                    value: CATALYST_USER_ROLES.user,
                  },
                  {
                    label: CATALYST_USER_ROLES.tablet,
                    value: CATALYST_USER_ROLES.tablet,
                  },
                ]}
              />
            )}

            {table.getColumn('status') && (
              <DataTableFacetedFilter
                column={table.getColumn('status')}
                title="Estado"
                options={[
                  { label: 'Ativo', value: true },
                  { label: 'Inativo', value: false },
                ]}
              />
            )}
          </DataTableToolbar>

          <div className="rounded-md border border-muted-foreground/35">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {queryUsers.isRefetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : queryUsers.isRefetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Error />
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      {table.getState().columnFilters.length
                        ? 'Não foram encontrados resultados.'
                        : 'Não existem quaisquer Utilizadores na base de dados.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <DataTableFooter table={table} />
        </div>
      )}
    </>
  );
}
